import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Eye, AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

const reviewBadge = (r) => {
  if (r.status === 'reviewed') return ['Approved', 'bg-green-500/15 text-green-400'];
  if (r.status === 'checking' || r.status === 'generating') return ['NAVI checking…', 'bg-sky-500/15 text-sky-300'];
  if (r.status === 'pending') return [`${r.flag_count || 0} item(s) to check`, 'bg-amber-500/15 text-amber-300'];
  return ['Not reviewed', 'bg-zinc-700 text-zinc-300'];
};

export default function CourseQaList({ programmeId, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [review, setReview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/instructor/programmes/${programmeId}/courses`, { headers });
      setRows(data.courses || []);
    } catch (e) { toast.error('Could not load courses'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId]);

  useEffect(() => { load(); }, [load]);

  const openReview = async (row) => {
    try {
      const { data } = await axios.get(`${API}/instructor/programmes/${programmeId}/courses/${row.course_id}/review`, { headers });
      setReview({ ...data, row });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not open review'); }
  };

  const approve = async (row) => {
    try {
      await axios.post(`${API}/instructor/programmes/${programmeId}/courses/${row.course_id}/approve`, {}, { headers });
      toast.success('Course content approved');
      setReview(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not approve'); }
  };

  return (
    <div className="space-y-3" data-testid="course-qa-list">
      {rows.length === 0 && <p className="text-sm text-zinc-500">No courses in this programme yet.</p>}
      {rows.map((r) => {
        const [label, cls] = reviewBadge(r.content_review || {});
        return (
          <div key={r.activity_id} className="flex flex-col md:flex-row md:items-center gap-3 p-4 rounded-xl border border-zinc-800 bg-zinc-900/60" data-testid={`qa-course-row-${r.activity_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{r.title}</p>
              <p className="text-xs text-zinc-500 mt-1">{r.period_title}{r.instructor ? ` · Instructor: ${r.instructor.name}` : ''}</p>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs w-fit ${cls}`} data-testid={`qa-course-status-${r.activity_id}`}>{label}</span>
            <div className="flex gap-2">
              {r.course_id && <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => window.open(`/courses/${r.course_id}/learn?programme=${programmeId}&activity=${r.activity_id}`, '_blank')} data-testid={`qa-course-open-${r.activity_id}`}><ExternalLink className="h-4 w-4 mr-1" />Open</Button>}
              {r.can_approve && <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => openReview(r)} data-testid={`qa-course-review-${r.activity_id}`}><Eye className="h-4 w-4 mr-1" />Review</Button>}
            </div>
          </div>
        );
      })}
      <ReviewDialog review={review} onClose={() => setReview(null)} onApprove={approve} />
    </div>
  );
}

function ReviewDialog({ review, onClose, onApprove }) {
  const flagged = (review?.sections || []).filter((s) => s.fact_flags.length);
  return (
    <Dialog open={!!review} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qa-review-dialog">
        <DialogHeader><DialogTitle>{review?.title}</DialogTitle></DialogHeader>
        {flagged.length === 0 && !(review?.quiz_flags || []).length ? (
          <p className="text-sm text-zinc-400">NAVI found nothing to check in this course. Open it to read through, then approve.</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">NAVI couldn't confirm these statements against trusted sources. Check each one before approving.</p>
            {flagged.map((s) => (
              <div key={s.index} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                <p className="text-sm font-medium text-amber-200 mb-2">{s.title}</p>
                {s.fact_flags.map((f, i) => (
                  <div key={i} className="flex gap-2 text-sm mb-2"><AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                    <div><p className="text-zinc-200">{f.quote ? `"${f.quote}"` : 'Section'}</p><p className="text-xs text-zinc-500">{f.reason}</p></div>
                  </div>
                ))}
              </div>
            ))}
            {(review?.quiz_flags || []).length > 0 && <p className="text-xs text-amber-300">{review.quiz_flags.length} quiz question(s) also flagged.</p>}
          </div>
        )}
        <div className="flex justify-end pt-2">
          <Button onClick={() => onApprove(review.row)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="qa-approve-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Approve content</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
